import { useState, useEffect, useCallback } from 'react';
import { supabase, isDemoMode } from '../lib/supabase';
import { queryCache, CACHE_TTL } from '../lib/cache';
import { requestThrottle } from '../lib/requestThrottle';
import { handleError } from '../lib/errorHandler';
import { fetchCachedUserProfile, patchCachedUserProfile } from '../lib/userProfileCache';
import { invalidateUserOrganizationRole } from '../lib/userOrganizationRoleCache';

export interface Organization {
  id: string;
  name: string;
  slug: string;
  description?: string;
  meta_description?: string;
  createdAt: Date;
  updatedAt: Date;
  settings?: {
    theme?: {
      primary?: string;
      secondary?: string;
    };
    fundraising?: {
      enabled?: boolean;
    };
    competitionTypes?: {
      lms?: { enabled?: boolean; label?: string };
      predictor?: { enabled?: boolean; label?: string };
      worldcup?: { enabled?: boolean; label?: string };
    };
  };
  logoUrl?: string;
  welcome_message?: string;
  about_text?: string;
  is_free_to_play?: boolean;
  primary_color?: string;
  secondary_color?: string;
}

export const useOrganizations = () => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOrganizations = async (force = false) => {
    if (isDemoMode) {
      const mockOrganizations: Organization[] = [
        {
          id: 'demo-org-1',
          name: 'Demo Organization',
          slug: 'demo-org',
          description: 'A demo organization for testing',
          createdAt: new Date(),
          updatedAt: new Date()
        }
      ];
      setOrganizations(mockOrganizations);
      setLoading(false);
      return;
    }

    // v2: Added logo_url support
    const cacheKey = queryCache.generateKey('organizations', { v: 2 });

    if (!force) {
      const cached = queryCache.get<Organization[]>(cacheKey);
      if (cached) {
        setOrganizations(cached);
        setLoading(false);
        return;
      }
    }

    if (!requestThrottle.shouldAllowRequest('organizations')) {
      console.warn('[useOrganizations] Request throttled - using existing data');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const result = await queryCache.deduplicate(
        cacheKey,
        async () => {
          const { data, error } = await supabase
            .from('organizations')
            .select('*')
            .order('created_at', { ascending: true });

          if (error) {
            console.error('Supabase query error:', error);
            throw error;
          }

          const formattedOrganizations: Organization[] = data.map(row => ({
            id: row.id,
            name: row.name,
            slug: row.slug,
            description: row.description || undefined,
            meta_description: row.meta_description || undefined,
            settings: row.settings || undefined,
            createdAt: new Date(row.created_at),
            updatedAt: new Date(row.updated_at),
            logoUrl: row.logo_url || undefined,
            welcome_message: row.welcome_message || undefined,
            about_text: row.about_text || undefined,
            is_free_to_play: row.is_free_to_play || false,
            primary_color: row.primary_color || undefined,
            secondary_color: row.secondary_color || undefined
          }));

          return formattedOrganizations;
        },
        CACHE_TTL.LONG
      );

      setOrganizations(result);

      // DO NOT preload images here - let components handle it on-demand
      // This was causing constant Supabase Storage requests
    } catch (err) {
      console.error('Error fetching organizations:', err);
      handleError(err, {
        hook: 'useOrganizations',
        action: 'fetchOrganizations'
      });
      if (err instanceof TypeError && err.message.includes('Failed to fetch')) {
        setError('Unable to connect to the database. Please check your internet connection and try again.');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load organizations');
      }
    } finally {
      setLoading(false);
    }
  };

  const getOrganizationBySlug = useCallback(async (slug: string): Promise<Organization | null> => {
    console.log('getOrganizationBySlug called with slug:', slug);
    console.log('isDemoMode:', isDemoMode);

    if (isDemoMode) {
      console.log('In demo mode, checking slug');
      // In demo mode, return mock organization if slug matches
      if (slug === 'demo-org' || slug === 'default-org') {
        return {
          id: 'demo-org-1',
          name: 'Demo Organization',
          slug: slug,
          description: 'A demo organization for testing',
          createdAt: new Date(),
          updatedAt: new Date()
        };
      }
      console.log('Demo mode: slug does not match, returning null');
      return null;
    }

    const cacheKey = queryCache.generateKey('organization_by_slug', { slug });

    try {
      console.log('useOrganizations: Fetching org with slug:', slug);

      const result = await queryCache.deduplicate(
        cacheKey,
        async () => {
          const { data, error } = await supabase
            .from('organizations')
            .select('*')
            .ilike('slug', slug)
            .single();

          console.log('useOrganizations: Query result:', { data, error });

          if (error) {
            if (error.code === 'PGRST116') {
              // No rows returned
              console.log('useOrganizations: No rows found for slug:', slug);
              return null;
            }
            console.error('useOrganizations: Query error:', error);
            throw error;
          }

          return {
            id: data.id,
            name: data.name,
            slug: data.slug,
            description: data.description || undefined,
            meta_description: data.meta_description || undefined,
            settings: data.settings || undefined,
            createdAt: new Date(data.created_at),
            updatedAt: new Date(data.updated_at),
            logoUrl: data.logo_url || undefined,
            welcome_message: data.welcome_message || undefined,
            about_text: data.about_text || undefined,
            is_free_to_play: data.is_free_to_play || false,
            primary_color: data.primary_color || undefined,
            secondary_color: data.secondary_color || undefined
          };
        },
        CACHE_TTL.LONG
      );

      return result;
    } catch (err) {
      console.error('Error fetching organization by slug:', err);
      handleError(err, {
        hook: 'useOrganizations',
        action: 'getOrganizationBySlug',
        metadata: { slug }
      });
      return null;
    }
  }, []);

  const updateUserCurrentOrganization = useCallback(async (userId: string, organizationId: string) => {
    if (isDemoMode) {
      // In demo mode, just return success
      return { error: null };
    }

    try {
      // Skip the UPDATE if the user's row already points at this org. Every
      // page mount used to issue a redundant write, racking up ~38k UPDATEs/h
      // and contending on the single users row.
      const existing = await fetchCachedUserProfile(userId);

      if (!existing || existing.current_organization_id !== organizationId) {
        const { error } = await supabase
          .from('users')
          .update({ current_organization_id: organizationId })
          .eq('id', userId);

        if (error) {
          console.error('Error updating users.current_organization_id:', error);
          return { error };
        }
        patchCachedUserProfile(userId, { current_organization_id: organizationId });
      }
      // Use upsert to handle the case where membership already exists
      // This avoids race conditions and 409 conflicts
      const { error: membershipError } = await supabase
        .from('user_organizations')
        .upsert(
          {
            user_id: userId,
            organization_id: organizationId,
            role: 'member',
            access_scope: 'footypicks'
          },
          {
            onConflict: 'user_id,organization_id,access_scope',
            ignoreDuplicates: true
          }
        );

      if (membershipError) {
        console.error('Failed to upsert organization membership:', membershipError);
        // Don't return error here as the main operation (updating current org) succeeded
        // This is just to ensure membership is recorded
      } else {
        invalidateUserOrganizationRole(userId, organizationId);
      }
      return { error: null };
    } catch (err) {
      console.error('Error updating user current organization:', err);
      return { error: err };
    }
  }, []);

  useEffect(() => {
    console.log('[useOrganizations] Hook mounted/re-executed');
    // v2: Added logo_url support
    const cacheKey = queryCache.generateKey('organizations', { v: 2 });
    const cached = queryCache.get<Organization[]>(cacheKey);

    if (cached) {
      console.log('[useOrganizations] Using cached data, skipping network request');
      setOrganizations(cached);
      setLoading(false);
    } else {
      console.log('[useOrganizations] No cache found, fetching from network');
      fetchOrganizations();
    }
  }, []);

  return {
    organizations,
    loading,
    error,
    getOrganizationBySlug,
    updateUserCurrentOrganization,
    refreshOrganizations: () => fetchOrganizations(true)
  };
};